import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import { FaEye, FaTrash, FaCheck, FaPlus, FaSignOutAlt, FaEdit, FaSearch } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import 'react-quill/dist/quill.snow.css';
import './Admin.css';

const quillModules = {
  toolbar: {
    container: [
      [{ header: [1, 2, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ script: 'sub' }, { script: 'super' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ direction: 'rtl' }],
      [{ size: ['small', false, 'large', 'huge'] }],
      [{ color: [] }, { background: [] }],
      [{ font: [] }],
      [{ align: [] }],
      ['link', 'image', 'video'],
      ['clean'],
    ],
    handlers: {
      image: function () {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.click();

        input.onchange = () => {
          const file = input.files[0];
          if (file) {
            if (file.size > 1024 * 1024) { // 1MB
              alert('Image size should be less than 1MB');
              return;
            }

            const reader = new FileReader();
            reader.onload = () => {
              const range = this.quill.getSelection();
              this.quill.insertEmbed(range.index, 'image', reader.result);
            };
            reader.readAsDataURL(file);
          }
        };
      }
    }
  }
};

const Admin = () => {
  const [posts, setPosts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [newPost, setNewPost] = useState({ title: '', content: '' });
  const [loading, setLoading] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (!user?.token) {
      navigate('/login');
    } else {
      fetchPosts();
    }
  }, [navigate]);

  const fetchPosts = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      const res = await fetch('https://api.dikshasea.in/api/admin/posts', {
        headers: { Authorization: `Bearer ${user.token}` },
      });

      const data = await res.json();
      setPosts(Array.isArray(data) ? data : data.data || []);
    } catch (err) {
      setError('Failed to fetch posts');
    }
  };

  const handleApiCall = async (url, method, body = null) => {
    const user = JSON.parse(localStorage.getItem('user'));
    const options = {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${user.token}`,
      },
    };
    if (body) options.body = JSON.stringify(body);

    const res = await fetch(url, options);
    if (!res.ok) throw new Error('Request failed');
    return res.json();
  };

  const handlePublish = async (postId) => {
    await handleApiCall(`https://api.dikshasea.in/api/admin/posts/${postId}/publish`, 'PATCH');
    fetchPosts();
  };

  const handleDelete = async (postId) => {
    await handleApiCall(`https://api.dikshasea.in/api/admin/posts/${postId}`, 'DELETE');
    fetchPosts();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await handleApiCall('https://api.dikshasea.in/api/admin/posts', 'POST', {
      ...newPost,
      status: 'published',
    });
    setNewPost({ title: '', content: '' });
    setShowAddForm(false);
    fetchPosts();
  };

  const handleUpdatePost = async () => {
    if (!selectedPost?.id || !selectedPost?.title || !selectedPost?.content) {
      setError('Missing required fields');
      return;
    }

    try {
      setLoading(true);
      await handleApiCall(`https://api.dikshasea.in/api/admin/posts/${selectedPost.id}`, 'PUT', {
        title: selectedPost.title,
        content: selectedPost.content,
      });
      setShowModal(false);
      setIsEditing(false);
      fetchPosts();
    } catch (error) {
      setError('Failed to update post');
    } finally {
      setLoading(false);
    }
  };

  const filteredPosts = Array.isArray(posts)
    ? posts.filter(
        (post) =>
          post.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          post.content?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  return (
    <motion.div 
      className="admin-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="admin-header">
        <motion.h1 
          className="admin-title"
          initial={{ y: -50 }}
          animate={{ y: 0 }}
          transition={{ type: 'spring', stiffness: 120 }}
        >
          Admin Dashboard
        </motion.h1>
        {/* <motion.button 
          className="admin-logout-btn"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => {
            localStorage.removeItem('user');
            navigate('/login');
          }}
        >
          <FaSignOutAlt /> Logout
        </motion.button> */}
      {/* </div> */}

      <AnimatePresence>
        {error && (
          <motion.div 
            className="error-banner"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {error}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="search-bar-container">
        <motion.div 
          className="search-input-wrapper"
          whileFocus={{ scale: 1.02 }}
        >
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search posts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </motion.div>
        <motion.button 
          className="add-post-btn"
          whileHover={{ scale: 1.05, boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setShowAddForm(true)}
        >
          <FaPlus /> Add Post
        </motion.button>
         <motion.button 
          className="admin-logout-btn"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => {
            localStorage.removeItem('user');
            navigate('/login');
          }}
        >
          <FaSignOutAlt /> Logout
        </motion.button>
      </div>
      </div>

      <AnimatePresence>
        {showAddForm && (
          <motion.form 
            onSubmit={handleSubmit} 
            className="add-post-form"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <h2>Create New Post</h2>
            <input
              type="text"
              placeholder="Post Title"
              value={newPost.title}
              onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
              required
            />
            <ReactQuill
              theme="snow"
              value={newPost.content}
              onChange={(value) => setNewPost({ ...newPost, content: value })}
              modules={quillModules}
            />
            <div className="form-buttons">
              <motion.button 
                type="submit" 
                className="publish-btn"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                Publish
              </motion.button>
              <motion.button 
                type="button" 
                onClick={() => setShowAddForm(false)} 
                className="cancel-btn"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                Cancel
              </motion.button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>

      {/* <h2 className="manage-posts-title">Manage Posts</h2> */}
      {filteredPosts.length === 0 ? (
        <motion.p 
          className="no-posts-message"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          No posts found.
        </motion.p>
      ) : (
        <motion.div 
          className="table-container"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
        >
          <table className="posts-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Author</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPosts.map((post, index) => (
                <motion.tr 
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    transition: { duration: 0.2 }
                  }}
                >
                  <td className="post-title-cell">{post.title}</td>
                  <td>{post.author_name}</td>
                  <td className={`post-status ${post.status === 'published' ? 'published' : 'draft'}`}>
                    <span className="status-badge">{post.status}</span>
                  </td>
                  <td className="actions-cell">
                    <motion.button
                      className="action-btn view-icon"
                      style={{ color: 'blue'}}
                      whileHover={{ scale: 1.2, color: '#4fc3f7' }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => {
                        setSelectedPost(post);
                        setShowModal(true);
                        setIsEditing(false);
                      }}
                      title="View"
                    >
                      <FaEye />
                    </motion.button>
                    {post.status !== 'published' && (
                      <motion.button 
                        className="action-btn publish-icon"
                        style={{ color: 'green' }}
                        whileHover={{ scale: 1.2, color: '#66bb6a' }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => handlePublish(post.id)}
                        title="Publish"
                      >
                        <FaCheck />
                      </motion.button>
                    )}
                    <motion.button 
                      className="action-btn delete-icon"
                      style={{ color:'red'}}
                      whileHover={{ scale: 1.2, color: '#ef5350' }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => handleDelete(post.id)}
                      title="Delete"
                    >
                      <FaTrash />
                    </motion.button>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </motion.div>
      )}

      <AnimatePresence>
        {showModal && selectedPost && (
          <motion.div 
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowModal(false)}
          >
            <motion.div 
              className="modal-content"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>
                  {isEditing ? (
                    <input
                      type="text"
                      value={selectedPost.title}
                      onChange={(e) => setSelectedPost({ ...selectedPost, title: e.target.value })}
                      className="modal-title-input"
                    />
                  ) : (
                    selectedPost.title
                  )}
                </h2>
                <div className="modal-meta">
                  <p><strong>Author:</strong> {selectedPost.author_name}</p>
                  <p><strong>Status:</strong> {selectedPost.status}</p>
                </div>
              </div>

              <div className="modal-body">
                {isEditing ? (
                  <ReactQuill
                    theme="snow"
                    value={selectedPost.content}
                    onChange={(value) => setSelectedPost({ ...selectedPost, content: value })}
                    modules={quillModules}
                  />
                ) : (
                  <div 
                    className="post-content-preview" 
                    dangerouslySetInnerHTML={{ __html: selectedPost.content }} 
                  />
                )}
              </div>

              <div className="modal-buttons">
                {isEditing ? (
                  <>
                    <motion.button 
                      onClick={() => setIsEditing(false)}
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      Cancel
                    </motion.button>
                    <motion.button 
                      onClick={handleUpdatePost}
                      className="save-btn"
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      {loading ? 'Saving...' : 'Save'}
                    </motion.button>
                  </>
                ) : (
                  <motion.button 
                    onClick={() => setIsEditing(true)}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    <FaEdit /> Edit
                  </motion.button>
                )}
                <motion.button 
                  onClick={() => setShowModal(false)}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  Close
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Admin;